import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from '../services/AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';
import { useSettings } from './SettingsContext';
import { creditDecimalToMicrocredits, microcreditsToCreditDecimal, sumMicrocredits } from '../utils/creditUnits';
import { defaultDefinitions, featureName } from '../utils/featureCatalog';
import { FeatureCatalogContext } from './FeatureCatalogContext';

const defaultFeatures = Object.fromEntries(defaultDefinitions.filter(definition => definition.kind === 'feature').map(definition => [definition.key, false]));

const FeatureContext = createContext({
    features: defaultFeatures,
    entitlements: null,
    isLoading: false,
    hasLoaded: false,
    refreshEntitlements: () => { },
    refreshCredits: () => { },
    definitions: defaultDefinitions,
    featureName,
});


function applyCreditBalance(data, credits) {
    if (credits?.unit?.microcreditsPerCredit) {
        const lots = credits?.lots || [];
        const fromPlanMicrocredits = sumMicrocredits(lots
            .filter((lot) => lot.source === 'allowance')
            .map((lot) => lot.remainingMicrocredits || creditDecimalToMicrocredits(lot.remainingCredits || '0')));
        const purchasedMicrocredits = sumMicrocredits(lots
            .filter((lot) => lot.source === 'purchased')
            .map((lot) => lot.remainingMicrocredits || creditDecimalToMicrocredits(lot.remainingCredits || '0')));
        const periodAllocationMicrocredits = credits.allowance?.grantedMicrocredits || sumMicrocredits(lots
            .filter((lot) => lot.source === 'allowance')
            .map((lot) => lot.grantedMicrocredits || creditDecimalToMicrocredits(lot.grantedCredits || '0')));
        const balanceMicrocredits = credits.balance?.availableMicrocredits
            || creditDecimalToMicrocredits(credits.balance?.availableCredits || '0');
        const reservedMicrocredits = credits.balance?.reservedMicrocredits
            || creditDecimalToMicrocredits(credits.balance?.reservedCredits || '0');
        return {
            ...data,
            aiCredits: {
                ...data.aiCredits,
                balance: microcreditsToCreditDecimal(balanceMicrocredits),
                balanceMicrocredits,
                reserved: microcreditsToCreditDecimal(reservedMicrocredits),
                reservedMicrocredits,
                deferredMicrocredits: credits.balance?.deferredMicrocredits || '0',
                overrunPolicy: credits.overrunPolicy || 'reservation-cap-v1',
                fromPlan: microcreditsToCreditDecimal(fromPlanMicrocredits),
                fromPlanMicrocredits,
                purchased: microcreditsToCreditDecimal(purchasedMicrocredits),
                purchasedMicrocredits,
                periodAllocation: microcreditsToCreditDecimal(periodAllocationMicrocredits),
                periodAllocationMicrocredits,
                commercialMode: credits.mode,
                lots,
            },
            creditHistory: credits.history || [],
        };
    }
    return data;
}

export const FeatureProvider = ({ children }) => {
    const { companyId, registerRefreshFunction } = useSettings();
    const [features, setFeatures] = useState(defaultFeatures);
    const [entitlements, setEntitlements] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [definitions, setDefinitions] = useState(defaultDefinitions);
    useEffect(() => {
        let active = true;
        axios.get(getApiUrl('subscription/features')).then(({ data }) => {
            if (active && Array.isArray(data?.definitions)) setDefinitions(data.definitions);
        }).catch(() => { /* Bundled catalog keeps verified labels available offline. */ });
        return () => { active = false; };
    }, []);
    const mountedRef = useRef(true);
    const companyRef = useRef(companyId);
    companyRef.current = companyId;
    const loadedCompanyRef = useRef(null);
    const inFlightRef = useRef(null);

    const refreshCredits = useCallback(async () => {
        if (!companyId) return;
        try {
            const { data } = await axios.get(getApiUrl(`${companyId}/subscription/credits`), { withCredentials: true });
            if (mountedRef.current && companyRef.current === companyId) {
                setEntitlements(current => current ? applyCreditBalance(current, data) : current);
            }
        } catch (_) { /* Keep the last confirmed balance; a later refresh retries. */ }
    }, [companyId]);

    const fetchEntitlements = useCallback(() => {
        if (!companyId) {
            // No company context yet — the id is extracted from the URL in a
            // SettingsContext effect, so on a hard refresh this runs once
            // before it exists. Do NOT mark loaded here: `hasLoaded` with the
            // all-false defaults reads as "this tenant has no features", and
            // every gated route redirects (the refresh→/feature-disabled
            // regression). Guards only mount under /:companyId, so a real id
            // always follows; until then they wait.
            return;
        }
        // Focus and visibilitychange often arrive together. Share the request,
        // and reserve the page's loading state for the initial company load.
        if (inFlightRef.current?.companyId === companyId) return inFlightRef.current.promise;
        const request = { companyId, promise: null };
        inFlightRef.current = request;
        const isCurrent = () => mountedRef.current && companyRef.current === companyId && inFlightRef.current === request;
        request.promise = (async () => {

            try {
                if (loadedCompanyRef.current !== companyId) setIsLoading(true);

                const { data } = await axios.get(getApiUrl(`${companyId}/entitlements`), { withCredentials: true });
                if (!data?.features) throw new Error('Entitlement response is incomplete');
                let resolved = data;
                try {
                    const credits = await axios.get(getApiUrl(`${companyId}/subscription/credits`), { withCredentials: true });
                    resolved = applyCreditBalance(data, credits.data);
                } catch (_) { /* Exact balances can retry independently. */ }
                if (!isCurrent()) return;
                setFeatures({ ...defaultFeatures, ...data.features });
                setEntitlements(resolved);
            } catch (error) {
                console.error('Error fetching features:', error);
                if (isCurrent()) {
                    setFeatures(defaultFeatures);
                    setEntitlements(null);
                }
            } finally {
                if (isCurrent()) {
                    loadedCompanyRef.current = companyId;
                    inFlightRef.current = null;
                    setIsLoading(false);
                    setHasLoaded(true);
                }
            }
        })();
        return request.promise;
    }, [companyId]);

    // A different company's flags are unknown until fetched — while they load
    // the guards must wait, not evaluate the previous company's answers.
    useEffect(() => {
        loadedCompanyRef.current = null;
        inFlightRef.current = null;
        setHasLoaded(false);
        setEntitlements(null);
        setFeatures(defaultFeatures);
    }, [companyId]);

    useEffect(() => {
        mountedRef.current = true;
        fetchEntitlements();

        if (registerRefreshFunction) {
            const unregister = registerRefreshFunction('features', fetchEntitlements);
            return () => {
                mountedRef.current = false;
                inFlightRef.current = null;
                if (unregister) unregister();
            };
        }

        return () => {
            mountedRef.current = false;
            inFlightRef.current = null;
        };
    }, [companyId, fetchEntitlements, registerRefreshFunction]);

    useEffect(() => {
        if (!entitlements?.validUntil) return undefined;
        const delay = Math.max(1000, Math.min(60000, new Date(entitlements.validUntil).getTime() - Date.now()));
        const timer = setTimeout(fetchEntitlements, delay);
        return () => clearTimeout(timer);
    }, [entitlements, fetchEntitlements]);
    useEffect(() => {
        let lastFocusRefresh = 0;
        const refresh = () => {
            if (document.visibilityState !== 'visible' || Date.now() - lastFocusRefresh < 1000) return;
            lastFocusRefresh = Date.now();
            fetchEntitlements();
        };
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', refresh);
        return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
    }, [fetchEntitlements]);

    const value = {
        features,
        entitlements,
        isLoading,
        hasLoaded,
        refreshEntitlements: fetchEntitlements,
        refreshCredits,
        definitions,
        featureName: key => featureName(key, definitions),
    };

    return (
        <FeatureCatalogContext.Provider value={{definitions,featureName:key=>featureName(key,definitions)}}><FeatureContext.Provider value={value}>
            {children}
        </FeatureContext.Provider></FeatureCatalogContext.Provider>
    );
};

export const useFeatures = () => useContext(FeatureContext);

export default FeatureContext;
